import type { ReactElement } from "react";
import type { Omit } from "utility-types";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import useOffsetPagination from "../../lib/hooks/useOffsetPagination";
import type { IBrazilianRealEstateFundGalleryPicture } from "../../types/graphqlTypes";
import LeftChevronIcon from "../icons/LeftChevronIcon";
import RightChevronIcon from "../icons/RightChevronIcon";
import * as palette from "../palette";
import ProgressiveImage from "../shared/ProgressiveImage";

interface IGalleryCarouselProps {
  realEstateFund: IBrazilianRealEstateFundTopLevelFragment;
  onPictureClick: (picture: Omit<IBrazilianRealEstateFundGalleryPicture, "__typename">) => void;
}

export default function GalleryCarousel({ realEstateFund, onPictureClick }: IGalleryCarouselProps): ReactElement {
  const pagination = useOffsetPagination({
    items: realEstateFund.gallery,
    itemsPerPage: 8,
  });

  return (
    <div
      css={{
        position: "relative",
        overflow: "hidden",
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
      }}
    >
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(8, 120px)",
          gridColumnGap: 4,
          "& > img:first-of-type": {
            pointerEvents: pagination.hasPreviousPage ? "none" : "all",
          },
          "& > img:last-of-type": {
            pointerEvents: pagination.hasNextPage ? "none" : "all",
          },
        }}
      >
        {pagination.currentItems.map((picture) => {
          return (
            <ProgressiveImage key={picture.thumbnailURL} src={picture.thumbnailURL} placeholder="">
              {({ loading }) => {
                return loading ? (
                  <div
                    title={`Carregando imagem de um imóvel do FII ${picture.alternativeText || realEstateFund.name}`}
                    css={{
                      width: 120,
                      height: 120,
                      backgroundColor: picture.dominantColor || undefined,
                    }}
                  />
                ) : (
                  <img
                    role="presentation"
                    onClick={() => onPictureClick(picture)}
                    src={picture.thumbnailURL}
                    alt={picture.alternativeText || `Imagem de um imóvel do FII ${realEstateFund.name}`}
                    width={120}
                    height={120}
                    draggable={false}
                    onContextMenu={(event) => event.preventDefault()}
                    css={{
                      userSelect: "none",
                      backgroundColor: picture.dominantColor || undefined,
                      objectFit: "cover",
                      cursor: "pointer",
                      height: 120,
                    }}
                  />
                );
              }}
            </ProgressiveImage>
          );
        })}
      </div>

      {pagination.hasPreviousPage && (
        <div
          css={{
            position: "absolute",
            transform: "translateY(-50%)",
            top: "50%",
            left: 24,
          }}
        >
          <button
            type="button"
            onClick={() => pagination.goToPreviousPage()}
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 42,
              height: 42,
              padding: 0,
              margin: 0,
              boxShadow: `0 0 35px 0 ${palette.mineShaft}`,
              backgroundColor: palette.royalBlue,
              border: "none",
              borderRadius: "100%",
              cursor: "pointer",
              outline: "none",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <LeftChevronIcon width={12} height={22} />
          </button>
        </div>
      )}

      {pagination.hasNextPage && (
        <div
          css={{
            position: "absolute",
            transform: "translateY(-50%)",
            top: "50%",
            right: 24,
          }}
        >
          <button
            type="button"
            onClick={() => pagination.goToNextPage()}
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 42,
              height: 42,
              padding: 0,
              margin: 0,
              boxShadow: `0 0 35px 0 ${palette.mineShaft}`,
              backgroundColor: palette.royalBlue,
              border: "none",
              borderRadius: "100%",
              cursor: "pointer",
              outline: "none",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <RightChevronIcon width={12} height={22} />
          </button>
        </div>
      )}
    </div>
  );
}
